import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import ScheduleModal from './ScheduleModal';
import { API_URL } from '../config';

// CSS pro z-index markerů
const markerStyles = `
  .current-location-marker {
    z-index: 1000 !important;
  }
  .selected-customer-marker {
    z-index: 800 !important;
  }
  .route-customer-marker {
    z-index: 900 !important;
  }
  .nearby-route-customer-marker {
    z-index: 700 !important;
  }
  .customer-marker {
    z-index: 100 !important;
  }
`;

// Přidání stylů do DOM
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = markerStyles;
  if (!document.head.querySelector('style[data-marker-styles]')) {
    styleElement.setAttribute('data-marker-styles', 'true');
    document.head.appendChild(styleElement);
  }
}

// Ikony pro markery - opravené SVG
const currentLocationIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30">
      <circle cx="12" cy="12" r="8" fill="red" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="12" r="3" fill="white"/>
    </svg>
  `),
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
  className: 'current-location-marker'
});

const customerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
      <circle cx="12" cy="12" r="8" fill="blue" stroke="white" stroke-width="1"/>
    </svg>
  `),
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10],
  className: 'customer-marker'
});

const selectedCustomerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="25">
      <circle cx="12" cy="12" r="8" fill="green" stroke="white" stroke-width="2"/>
    </svg>
  `),
  iconSize: [25, 25],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
  className: 'selected-customer-marker'
});

const routeCustomerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22">
      <circle cx="12" cy="12" r="8" fill="orange" stroke="white" stroke-width="2"/>
    </svg>
  `),
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -11],
  className: 'route-customer-marker'
});

const nearbyRouteCustomerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
      <circle cx="12" cy="12" r="8" fill="purple" stroke="white" stroke-width="2"/>
    </svg>
  `),
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10],
  className: 'nearby-route-customer-marker'
});

const meetingCustomerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22">
      <circle cx="12" cy="12" r="8" fill="lightgreen" stroke="white" stroke-width="2"/>
      <rect x="8" y="8" width="8" height="6" fill="white" rx="1"/>
      <rect x="9" y="10" width="6" height="1" fill="lightgreen"/>
      <rect x="9" y="12" width="4" height="1" fill="lightgreen"/>
    </svg>
  `),
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -11],
  className: 'meeting-customer-marker'
});

// Komponenta pro automatické centrování mapy na trasu
const FitBounds = ({ routePoints, shouldFitBounds, onFitComplete }) => {
  const map = useMap();

  useEffect(() => {
    if (shouldFitBounds && routePoints && routePoints.length > 0) {
      const validPoints = routePoints.filter(point => point.lat && point.lng);
      if (validPoints.length > 0) {
        const bounds = L.latLngBounds(validPoints.map(point => [point.lat, point.lng]));
        map.fitBounds(bounds, { padding: [50, 50] });
        onFitComplete && onFitComplete();
      }
    }
  }, [routePoints, shouldFitBounds, map, onFitComplete]);

  return null;
};

// Stabilní routing komponenta
const RoutingMachine = ({ routePoints, onRouteFound }) => {
  const map = useMap();
  const routingControlRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const previousRoutePointsRef = useRef(null);
  const routeStableRef = useRef(false);
  const routeCreationTimeoutRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Pokud není dostatek bodů, odstraň trasu
    if (routePoints.length < 2) {
      if (routingControlRef.current) {
        try {
          map.removeControl(routingControlRef.current);
        } catch (e) {
          console.warn('Error removing route control:', e);
        }
        routingControlRef.current = null;
        routeStableRef.current = false;
      }
      setIsLoading(false);
      return;
    }

    // Vytvoř nové waypoints
    const newWaypoints = routePoints.map(point => L.latLng(point.lat, point.lng));
    
    // Porovnej s předchozími waypoints - přesnější porovnání
    const previousRoutePoints = previousRoutePointsRef.current;
    const routePointsChanged = !previousRoutePoints || 
      previousRoutePoints.length !== routePoints.length ||
      routePoints.some((point, i) => 
        !previousRoutePoints[i] ||
        Math.abs(previousRoutePoints[i].lat - point.lat) > 0.0001 ||
        Math.abs(previousRoutePoints[i].lng - point.lng) > 0.0001
      );

    // Pokud se nic nezměnilo a trasa je už stabilní, neprovádět nic
    if (!routePointsChanged && routeStableRef.current && routingControlRef.current) {
      return;
    }

    // Ulož aktuální body pro příští porovnání
    previousRoutePointsRef.current = [...routePoints];

    // Zruš předchozí timeout
    if (routeCreationTimeoutRef.current) {
      clearTimeout(routeCreationTimeoutRef.current);
    }

    // Odstraň existující routing control pouze pokud je potřeba
    if (routingControlRef.current && routePointsChanged) {
      try {
        map.removeControl(routingControlRef.current);
      } catch (e) {
        console.warn('Error removing existing route control:', e);
      }
      routingControlRef.current = null;
      routeStableRef.current = false;
    }

    // Pokud se body nezměnily, nesahej na trasu
    if (!routePointsChanged && routingControlRef.current) {
      return;
    }

    setIsLoading(true);

    // Vytvoř nový routing control s delším timeoutem pro stabilitu
    const createRouting = () => {
      try {
        routingControlRef.current = L.Routing.control({
          waypoints: newWaypoints,
          routeWhileDragging: false,
          createMarker: () => null, // Netvořit defaultní markery
          lineOptions: {
            styles: [{ 
              color: '#3b82f6', 
              weight: 5, 
              opacity: 0.8,
              dashArray: null // Plná čára
            }]
          },
          router: L.Routing.osrmv1({
            serviceUrl: 'https://router.project-osrm.org/route/v1',
            timeout: 15000 // Prodloužený timeout
          }),
          show: false, // Skryj instrukce
          addWaypoints: false, // Zakázat přidávání waypointů kliknutím
          draggableWaypoints: false, // Zakázat přetahování
          createMarker: () => null, // Žádné markery
          showAlternatives: false, // Žádné alternativní trasy
          fitSelectedRoutes: false, // Neupravovat zoom na trasu
          collapsible: false, // Nechat panel nezbytný
          plan: L.Routing.plan(newWaypoints, {
            createMarker: () => null, // Žádné markery v plánu
            draggableWaypoints: false,
            addWaypoints: false
          })
        }).on('routesfound', function(e) {
          const routes = e.routes;
          if (routes && routes[0] && routes[0].summary) {
            const summary = routes[0].summary;
            
            setIsLoading(false);
            routeStableRef.current = true; // Označit trasu jako stabilní
            
            // Skryj routing container pomocí CSS
            setTimeout(() => {
              const containers = document.querySelectorAll('.leaflet-routing-container');
              containers.forEach(container => {
                container.style.display = 'none';
              });
            }, 100);
            
            if (onRouteFound) {
              onRouteFound({
                distance: summary.totalDistance,
                time: summary.totalTime,
                coordinates: routes[0].coordinates
              });
            }
          }
        }).on('routingerror', function(e) {
          setIsLoading(false);
          routeStableRef.current = false;
          console.warn('Routing error:', e);
        });

        // Přidej na mapu
        if (map && routingControlRef.current) {
          routingControlRef.current.addTo(map);
        }
      } catch (error) {
        setIsLoading(false);
        routeStableRef.current = false;
        console.error('Error creating route:', error);
      }
    };

    // Malé zpoždění pro stabilitu s referenci na timeout
    routeCreationTimeoutRef.current = setTimeout(createRouting, 200);

    return () => {
      if (routeCreationTimeoutRef.current) {
        clearTimeout(routeCreationTimeoutRef.current);
      }
      // Neodstraňovat routing control v cleanup, pokud není nutné
      // - necháme ho existovat pro stabilitu
    };
  }, [map, routePoints, onRouteFound]);

  // Cleanup při unmount komponenty
  useEffect(() => {
    return () => {
      if (routeCreationTimeoutRef.current) {
        clearTimeout(routeCreationTimeoutRef.current);
      }
      if (routingControlRef.current && map) {
        try {
          map.removeControl(routingControlRef.current);
        } catch (e) {
          console.warn('Error in final cleanup:', e);
        }
        routingControlRef.current = null;
      }
    };
  }, []);

  return null;
};

// Hlavní komponenta
const MeetingPlanner = ({ companies, userId, isDarkMode, onClose, onRefreshCompanies, preselectedCustomer, statusFilter, uniqueStatuses, toggleStatusFilter, getStatusEmoji }) => {
  console.log('MeetingPlanner: Component rendered with props:', { 
    companiesCount: companies?.length, 
    userId, 
    isDarkMode,
    preselectedCustomer: preselectedCustomer?.name,
    statusFilter: statusFilter?.length
  });

  const [currentPosition, setCurrentPosition] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [routePoints, setRoutePoints] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]); // Skutečné koordináty trasy
  const [shouldFitBounds, setShouldFitBounds] = useState(false); // Trigger pro centrování mapy
  const [filterRadius, setFilterRadius] = useState(15); // km
  const [searchQuery, setSearchQuery] = useState('');
  const [isRouteMode, setIsRouteMode] = useState(false);
  const [nearbyCustomers, setNearbyCustomers] = useState([]);
  const [plannedMeetings, setPlannedMeetings] = useState([]);
  const [savedRoutes, setSavedRoutes] = useState([]); // Uložené trasy
  const [showSaveRouteModal, setShowSaveRouteModal] = useState(false);
  const [showLoadRouteModal, setShowLoadRouteModal] = useState(false);
  const [routeName, setRouteName] = useState(''); // Název trasy pro uložení
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCustomerForSchedule, setSelectedCustomerForSchedule] = useState(null);
  const [routeOrderForSchedule, setRouteOrderForSchedule] = useState(0);
  const [isUpdatingGps, setIsUpdatingGps] = useState(false); // Loading pro GPS aktualizaci
  const [gpsUpdateProgress, setGpsUpdateProgress] = useState(0); // Procenta pokroku 0-100
  const [showStatusDropdown, setShowStatusDropdown] = useState(false); // Pro status filter dropdown
  const [isRefreshingMyLocation, setIsRefreshingMyLocation] = useState(false); // Loading pro aktualizaci vlastní polohy
  const [showManualLocationInput, setShowManualLocationInput] = useState(false);
  const [manualLocationAddress, setManualLocationAddress] = useState('');

  // Získání aktuální pozice
  useEffect(() => {
    console.log('MeetingPlanner: Requesting geolocation...');
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('MeetingPlanner: Geolocation success:', position.coords);
          setCurrentPosition({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('MeetingPlanner: Geolocation error:', error);
          // Výchozí pozice (Praha)
          setCurrentPosition({
            lat: 50.0755,
            lng: 14.4378
          });
        }
      );
    } else {
      console.warn('MeetingPlanner: Geolocation not supported');
      setCurrentPosition({
        lat: 50.0755,
        lng: 14.4378
      });
    }
  }, []);

  // Zavření dropdown při kliku mimo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusDropdown && !event.target.closest('.status-dropdown-container')) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  // UseEffect pro zpracování preselectedCustomer
  useEffect(() => {
    if (preselectedCustomer && currentPosition) {
      console.log('Processing preselected customer:', preselectedCustomer.name);
      
      // Přidej zákazníka do vybraných
      if (!selectedCustomers.find(c => c._id === preselectedCustomer._id)) {
        setSelectedCustomers([preselectedCustomer]);
      }

      // Pokud má zákazník GPS souřadnice, vytvoř trasu
      if (preselectedCustomer.lat && preselectedCustomer.lng) {
        console.log('Creating route to preselected customer');
        planRoute(preselectedCustomer);
      }
    }
  }, [preselectedCustomer, currentPosition, selectedCustomers]);

  // UseEffect pro hledání zákazníků v okolí trasy při změně routeCoordinates
  useEffect(() => {
    if (routeCoordinates.length >= 2 && routeInfo) {
      console.log('Triggering findNearbyCustomers with route coordinates:', routeCoordinates.length);
      // Malé zpoždění pro zajištění, že jsou routeCoordinates aktuální
      setTimeout(() => {
        findNearbyCustomers();
      }, 300);
    } else {
      // Pokud není trasa, vymaž nearby zákazníky
      setNearbyCustomers([]);
    }
  }, [routeCoordinates, routeInfo, companies, statusFilter, searchQuery, filterRadius]);

  // Načtení uložených tras z localStorage při startu
  useEffect(() => {
    const saved = localStorage.getItem(`savedRoutes_${userId}`);
    if (saved) {
      try {
        setSavedRoutes(JSON.parse(saved));
      } catch (error) {
        console.error('Chyba při načítání uložených tras:', error);
        setSavedRoutes([]);
      }
    }
  }, [userId]);

  // Výpočet vzdálenosti - musí být před použitím
  const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const d = R * c;
    return d;
  };

  const deg2rad = (deg) => {
    return deg * (Math.PI/180);
  };

  // Výpočet nejkratší vzdálenosti bodu od úsečky (zjednodušený algoritmus)
  const getDistanceFromPointToLineSegment = (pointLat, pointLng, line1Lat, line1Lng, line2Lat, line2Lng) => {
    // Pokud jsou oba body úsečky stejné, vrať vzdálenost k tomuto bodu
    if (Math.abs(line1Lat - line2Lat) < 0.0001 && Math.abs(line1Lng - line2Lng) < 0.0001) {
      return getDistanceFromLatLonInKm(pointLat, pointLng, line1Lat, line1Lng);
    }
    
    // Jednoduché aproximace - na krátké vzdálenosti můžeme použít Pythagorovu větu
    // Pro přesnější výsledky by bylo lepší použít geodetické výpočty
    
    // Vzdálenosti k oběma koncům úsečky
    const dist1 = getDistanceFromLatLonInKm(pointLat, pointLng, line1Lat, line1Lng);
    const dist2 = getDistanceFromLatLonInKm(pointLat, pointLng, line2Lat, line2Lng);
    
    // Vzdálenost mezi koncovými body úsečky
    const segmentLength = getDistanceFromLatLonInKm(line1Lat, line1Lng, line2Lat, line2Lng);
    
    // Pokud je úsečka velmi krátká, vrať nejmenší vzdálenost ke koncům
    if (segmentLength < 0.1) { // méně než 100m
      return Math.min(dist1, dist2);
    }
    
    // Použij projekci bodu na přímku pomocí dot product
    // Převod do lokálního souřadnicového systému (pro malé vzdálenosti OK)
    const x1 = 0, y1 = 0; // line1 jako počátek
    const x2 = (line2Lng - line1Lng) * Math.cos(deg2rad(line1Lat)) * 111.32; // přibližná konverze lng na km
    const y2 = (line2Lat - line1Lat) * 111.32; // přibližná konverze lat na km
    const x3 = (pointLng - line1Lng) * Math.cos(deg2rad(line1Lat)) * 111.32;
    const y3 = (pointLat - line1Lat) * 111.32;
    
    // Dot product a projekce
    const dotProduct = ((x3 - x1) * (x2 - x1) + (y3 - y1) * (y2 - y1));
    const segmentLengthSquared = (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1);
    
    if (segmentLengthSquared === 0) {
      return dist1; // úsečka je bod
    }
    
    const t = Math.max(0, Math.min(1, dotProduct / segmentLengthSquared));
    
    // Najdi nejbližší bod na úsečce
    const projectionX = x1 + t * (x2 - x1);
    const projectionY = y1 + t * (y2 - y1);
    
    // Vzdálenost od testovaného bodu k projekci
    const distance = Math.sqrt((x3 - projectionX) * (x3 - projectionX) + (y3 - projectionY) * (y3 - projectionY));
    
    return distance;
  };

  // Filtrování zákazníků podle vyhledávání
  const matchesSearch = (company) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      company.name?.toLowerCase().includes(query) ||
      company.ico?.includes(query) ||
      company.address?.toLowerCase().includes(query) ||
      company.status?.toLowerCase().includes(query)
    );
  };

  // Filtrování zákazníků podle vzdálenosti
  const isWithinRadius = (company) => {
    if (!currentPosition || !company.lat || !company.lng) return false;
    
    const distance = getDistanceFromLatLonInKm(
      currentPosition.lat, 
      currentPosition.lng, 
      company.lat, 
      company.lng
    );
    
    return distance <= filterRadius;
  };

  // Filtrování podle status filtru  
  const matchesStatusFilter = (company) => {
    // Pokud není status filter nastavený, zobraz všechno
    if (!statusFilter || statusFilter.length === 0) return true;
    // Pokud je status filter nastavený, zobraz jen firmy s odpovídajícím statusem
    return statusFilter.includes(company.status);
  };

  const filteredCompanies = useMemo(() => {
    return companies.filter(company => 
      matchesSearch(company) && matchesStatusFilter(company)
      // Zobraz všechny firmy, nejen ty s GPS souřadnicemi
    );
  }, [companies, searchQuery, statusFilter]);

  // Plánování trasy
  const planRoute = (targetCustomer) => {
    if (!currentPosition || !targetCustomer.lat || !targetCustomer.lng) {
      alert('Nelze naplánovat trasu - chybí GPS souřadnice');
      return;
    }

    // Pokud už existuje trasa, přidej zákazníka do ní
    if (isRouteMode && routePoints.length > 0) {
      addToRoute(targetCustomer);
      return;
    }

    // Jinak vytvoř novou trasu
    setIsRouteMode(true);
    
    const startPoint = { 
      lat: currentPosition.lat, 
      lng: currentPosition.lng, 
      type: 'start',
      company: { name: 'Výchozí pozice', address: 'Vaše aktuální pozice' }
    };
    
    const endPoint = { 
      lat: targetCustomer.lat, 
      lng: targetCustomer.lng, 
      type: 'customer', 
      customerId: targetCustomer._id,
      company: targetCustomer
    };
    
    setRoutePoints([startPoint, endPoint]);
    // Hledání zákazníků v okolí se nyní spouští až po úspěšném vytvoření trasy v handleRouteFound
  };

  // Najdi zákazníky v okolí trasy
  const findNearbyCustomers = () => {
    console.log('findNearbyCustomers called with routeCoordinates:', routeCoordinates.length);
    console.log('Total filtered companies to check:', filteredCompanies.length);
    
    if (routeCoordinates.length < 2) {
      console.log('Not enough route coordinates');
      setNearbyCustomers([]);
      return;
    }
    
    const nearby = [];
    const routeRadius = filterRadius; // Použij nastavený perimetr
    
    filteredCompanies.forEach(company => {
      if (!company.lat || !company.lng) return;
      
      // Kontrola, zda zákazník už není v trase
      const isInRoute = routePoints.some(point => point.customerId === company._id);
      if (isInRoute) return;
      
      // Vypočítej nejkratší vzdálenost od trasy (algoritmus point-to-line)
      let minDistanceFromRoute = Infinity;
      
      // Pokud má trasa málo bodů, přidej interpolované body pro lepší pokrytí
      let routeSegments = [];
      for (let i = 0; i < routeCoordinates.length - 1; i++) {
        const start = routeCoordinates[i];
        const end = routeCoordinates[i + 1];
        routeSegments.push([start, end]);
        
        // Pokud je vzdálenost mezi body velká (>5km), přidej interpolované body
        const segmentDistance = getDistanceFromLatLonInKm(start.lat, start.lng, end.lat, end.lng);
        if (segmentDistance > 5) {
          const steps = Math.floor(segmentDistance / 2); // každé 2 km přidej bod
          for (let step = 1; step < steps; step++) {
            const ratio = step / steps;
            const interpolatedLat = start.lat + (end.lat - start.lat) * ratio;
            const interpolatedLng = start.lng + (end.lng - start.lng) * ratio;
            routeSegments.push([
              {lat: interpolatedLat, lng: interpolatedLng},
              {lat: interpolatedLat, lng: interpolatedLng} // bod jako segment délky 0
            ]);
          }
        }
      }
      
      // Pro každý úsek trasy najdi nejkratší vzdálenost
      for (const [segmentStart, segmentEnd] of routeSegments) {
        const distance = getDistanceFromPointToLineSegment(
          company.lat, company.lng,
          segmentStart.lat, segmentStart.lng,
          segmentEnd.lat, segmentEnd.lng
        );
        
        if (distance < minDistanceFromRoute) {
          minDistanceFromRoute = distance;
        }
      }
      
      // Pokud je zákazník do nastaveného radiusu od trasy, přidej ho
      if (minDistanceFromRoute <= routeRadius) {
        console.log(`Customer ${company.nazev} is ${minDistanceFromRoute.toFixed(2)}km from route - ADDING`);
        nearby.push({
          ...company,
          distanceFromRoute: minDistanceFromRoute
        });
      } else {
        console.log(`Customer ${company.nazev} is ${minDistanceFromRoute.toFixed(2)}km from route - too far (limit: ${routeRadius}km)`);
      }
    });
    
    nearby.sort((a, b) => a.distanceFromRoute - b.distanceFromRoute);
    console.log('Final nearby customers count:', nearby.length);
    console.log('Setting nearbyCustomers to:', nearby.map(c => c.nazev));
    setNearbyCustomers(nearby);
  };

  // Přidání zákazníka do trasy
  const addToRoute = (customer) => {
    if (!routePoints.find(point => point.customerId === customer._id)) {
      const newPoint = { 
        lat: customer.lat, 
        lng: customer.lng, 
        type: 'customer', 
        customerId: customer._id,
        company: customer
      };
      
      // Pokud ještě není žádná trasa, vytvoř novou s výchozí pozicí
      if (routePoints.length === 0) {
        const startPoint = { 
          lat: currentPosition.lat, 
          lng: currentPosition.lng, 
          type: 'start',
          customerId: null,
          company: { name: 'Výchozí pozice', address: 'Vaše aktuální poloha' }
        };
        setRoutePoints([startPoint, newPoint]);
        setIsRouteMode(true);
      } else {
        // Přidej před poslední bod (který je cíl) nebo na konec pokud není cíl
        const newRoutePoints = [...routePoints];
        newRoutePoints.push(newPoint);
        setRoutePoints(newRoutePoints);
      }
      
      // Přidej do vybraných zákazníků
      if (!selectedCustomers.find(c => c._id === customer._id)) {
        setSelectedCustomers(prev => [...prev, customer]);
      }
    }
  };

  // Odebrání zákazníka z trasy
  const removeFromRoute = (index) => {
    const newRoutePoints = [...routePoints];
    newRoutePoints.splice(index, 1);
    setRoutePoints(newRoutePoints);
  };

  // Handle route found
  const handleRouteFound = (routeData) => {
    console.log('Route found with coordinates:', routeData.coordinates?.length);
    setRouteInfo(routeData);
    setRouteCoordinates(routeData.coordinates || []); // Uložit skutečné koordináty trasy
    // Spouštění hledání zákazníků se přesune do useEffect
  };

  // getStatusEmoji funkce je nyní předávaná jako prop z App.js

  // Funkce pro získání poslední aktivity
  const getLastActivity = (company) => {
    if (!company.activities || company.activities.length === 0) {
      return 'Žádná aktivita';
    }
    
    const lastActivity = company.activities[company.activities.length - 1];
    
    // Zkusíme různé možné názvy pro datum aktivity
    const activityDate = lastActivity.createdAt || lastActivity.date || lastActivity.nextActionDate;
    
    if (!activityDate) {
      return lastActivity.text || lastActivity.type || 'Neznámá aktivita';
    }
    
    try {
      const date = new Date(activityDate);
      if (isNaN(date.getTime())) {
        return lastActivity.text || lastActivity.type || 'Neznámá aktivita';
      }
      
      const formattedDate = date.toLocaleDateString('cs-CZ');
      const activityType = lastActivity.type || 'Aktivita';
      const activityText = lastActivity.text || '';
      
      // Pokud je text příliš dlouhý, zkrátíme ho
      const displayText = activityText.length > 50 ? 
        activityText.substring(0, 50) + '...' : 
        activityText;
      
      return `${activityType}: ${displayText} (${formattedDate})`;
    } catch (error) {
      console.error('Chyba při formátování data aktivity:', error);
      return lastActivity.text || lastActivity.type || 'Neznámá aktivita';
    }
  };

  // Vyčištění režimu trasy
  const clearRouteMode = () => {
    setIsRouteMode(false);
    setRoutePoints([]);
    setRouteInfo(null);
    setRouteCoordinates([]); // Vymaž koordináty trasy
    setNearbyCustomers([]);
    setSelectedCustomers([]); // Vymaž vybrané zákazníky
  };

  // Funkce pro správu tras
  const saveCurrentRoute = () => {
    if (routePoints.length < 2) {
      alert('Není co uložit - vytvořte nejdříve trasu s minimálně 2 body');
      return;
    }
    setShowSaveRouteModal(true);
  };

  const handleSaveRoute = () => {
    if (!routeName.trim()) {
      alert('Zadejte název trasy');
      return;
    }

    const routeData = {
      id: Date.now(),
      name: routeName.trim(),
      routePoints: routePoints,
      routeInfo: routeInfo,
      routeCoordinates: routeCoordinates,
      selectedCustomers: selectedCustomers,
      createdAt: new Date().toISOString(),
      createdBy: userId
    };

    const updatedRoutes = [...savedRoutes, routeData];
    setSavedRoutes(updatedRoutes);
    localStorage.setItem(`savedRoutes_${userId}`, JSON.stringify(updatedRoutes));
    
    setRouteName('');
    setShowSaveRouteModal(false);
    alert(`Trasa "${routeData.name}" byla uložena`);
  };

  const loadRoute = (route) => {
    setIsRouteMode(true);
    setRoutePoints(route.routePoints || []);
    setRouteInfo(route.routeInfo || null);
    setRouteCoordinates(route.routeCoordinates || []);
    setSelectedCustomers(route.selectedCustomers || []);
    setShowLoadRouteModal(false);
    
    // Trigger automatického centrování mapy na načtenou trasu
    setTimeout(() => {
      setShouldFitBounds(true);
    }, 100); // Krátké zpoždění pro vykreslení bodů
    
    console.log(`Načetena trasa: ${route.name}`);
  };

  const deleteRoute = (routeId) => {
    if (window.confirm('Opravdu chcete smazat totu trasu?')) {
      const updatedRoutes = savedRoutes.filter(route => route.id !== routeId);
      setSavedRoutes(updatedRoutes);
      localStorage.setItem(`savedRoutes_${userId}`, JSON.stringify(updatedRoutes));
    }
  };

  // Funkce pro změnu pořadí bodů v trase
  const moveRoutePointUp = (index) => {
    if (index <= 1) return; // Nelze posunout výchozí bod nebo už první zákazník
    
    const newRoutePoints = [...routePoints];
    [newRoutePoints[index], newRoutePoints[index - 1]] = [newRoutePoints[index - 1], newRoutePoints[index]];
    setRoutePoints(newRoutePoints);
  };

  const moveRoutePointDown = (index) => {
    if (index >= routePoints.length - 1) return; // Nelze posunout poslední bod
    
    const newRoutePoints = [...routePoints];
    [newRoutePoints[index], newRoutePoints[index + 1]] = [newRoutePoints[index + 1], newRoutePoints[index]];
    setRoutePoints(newRoutePoints);
  };

  // Otevření schedule modalu
  const openScheduleModal = (customer, routeOrder = 0) => {
    setSelectedCustomerForSchedule(customer);
    setRouteOrderForSchedule(routeOrder);
    setShowScheduleModal(true);
  };

  // Uložení naplánované schůzky
  const savePlannedMeeting = async (meetingData) => {
    try {
      const newMeeting = {
        id: Date.now(),
        ...meetingData,
        createdAt: new Date().toISOString()
      };
      
      // Přidáme schůzku do místního stavu
      setPlannedMeetings(prev => [...prev, newMeeting]);
      
      // Uložíme schůzku ako aktivitu do backendu (používame správny formát pre API)
      const activityText = meetingData.notes 
        ? `Schůzka naplánována na ${new Date(meetingData.scheduledTime).toLocaleString('cs-CZ')} - ${meetingData.notes}`
        : `Schůzka naplánována na ${new Date(meetingData.scheduledTime).toLocaleString('cs-CZ')}`;

      const response = await fetch(`${API_URL}/companies/${meetingData.customerId}/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: activityText,
          createdBy: userId,
          nextActionDate: meetingData.scheduledTime
        })
      });

      if (!response.ok) {
        throw new Error('Nepodařilo se uložit aktivitu');
      }

      console.log('Schůzka úspěšně uložena jako aktivita');
      
      // Načteme aktuální data firmy a aktualizujeme nextActionDate
      try {
        const companyResponse = await fetch(`${API_URL}/companies/${meetingData.customerId}`);
        if (!companyResponse.ok) {
          throw new Error('Nepodařilo se načíst data zákazníka');
        }
        
        const companyData = await companyResponse.json();
        
        // Aktualizujeme nextActionDate na čas schůzky
        const updateResponse = await fetch(`${API_URL}/companies/${meetingData.customerId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...companyData,
            nextActionDate: meetingData.scheduledTime
          })
        });

        if (!updateResponse.ok) {
          throw new Error('Nepodařilo se aktualizovat datum další akce zákazníka');
        }

        console.log('Datum další akce zákazníka úspěšně aktualizováno');
        
        // Refresh data z parent komponenty
        if (onRefreshCompanies) {
          onRefreshCompanies();
        }
        
      } catch (updateError) {
        console.error('Chyba při aktualizaci data další akce zákazníka:', updateError);
        // Toto není kritická chyba, takže nebudeme zobrazovat alert
      }
      
      alert('Schůzka byla úspěšně naplánována a uložena jako aktivita!');
      
    } catch (error) {
      console.error('Chyba při ukládání schůzky:', error);
      alert('Schůzka byla naplánována, ale nepodařilo se ji uložit jako aktivitu. Zkuste to prosím později.');
    }
    
    setShowScheduleModal(false);
    setSelectedCustomerForSchedule(null);
    setRouteOrderForSchedule(0);
  };

  // Funkce pro ruční aktualizaci vlastní polohy (když se zasekne)
  const refreshMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Váš prohlížeč nepodporuje geolokaci.');
      return;
    }
    setIsRefreshingMyLocation(true);

    // Pomocná funkce: IP fallback jako poslední záchrana
    const tryIpFallback = async () => {
      try {
        const resp = await fetch('https://ipapi.co/json/');
        const data = await resp.json();
        if (data.latitude && data.longitude) {
          setCurrentPosition({ lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) });
          setIsRefreshingMyLocation(false);
          alert('GPS zablokována – použita přibližná poloha dle IP adresy (přesnost ~město).');
          return;
        }
      } catch (_) { /* IP API nedostupná */ }
      setIsRefreshingMyLocation(false);
      alert(
        'Polohu se nepodařilo získat.\n\n' +
        'Zkuste:\n' +
        '• Klikněte na ikonu 🔒/ℹ️ vlevo v adresním řádku → Poloha → Povolit\n' +
        '• Nebo v Nastavení prohlížeče vyhledejte „Poloha" a povolte pro tento web\n' +
        '• Na iOS: Nastavení → Soukromí a zabezpečení → Poloha → Safari/Chrome → Při používání aplikace'
      );
    };

    // Krok 1: zkusit BEZ vysoké přesnosti (spolehlivější, nevyžaduje GPS čip)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentPosition({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsRefreshingMyLocation(false);
      },
      async (error) => {
        console.error('Geolocation error (low accuracy):', error.code, error.message);

        if (error.code === 1) {
          // PERMISSION_DENIED – zkusit IP fallback
          await tryIpFallback();
        } else if (error.code === 2) {
          // POSITION_UNAVAILABLE – zkusit ještě s vysokou přesností
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setCurrentPosition({ lat: position.coords.latitude, lng: position.coords.longitude });
              setIsRefreshingMyLocation(false);
            },
            async () => {
              setIsRefreshingMyLocation(false);
              alert('Poloha není dostupná. Zapněte GPS nebo lokalizaci v nastavení zařízení.');
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
          );
        } else {
          // TIMEOUT
          setIsRefreshingMyLocation(false);
          alert('Poloha vypršela (timeout). Zkuste to znovu nebo zkontrolujte GPS signál.');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
    );
  };

  // Funkce pro nastavení polohy z ručně zadané adresy
  const geocodeManualLocation = async () => {
    const addr = manualLocationAddress.trim();
    if (!addr) return;
    setIsRefreshingMyLocation(true);
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=1`,
        { headers: { 'Accept-Language': 'cs' } }
      );
      const data = await resp.json();
      if (data && data[0]) {
        setCurrentPosition({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
        setShowManualLocationInput(false);
        setManualLocationAddress('');
      } else {
        alert('Adresa nebyla nalezena. Zkuste ji upřesnit (např. „Václavské náměstí, Praha").');
      }
    } catch (_) {
      alert('Chyba při hledání adresy. Zkontrolujte připojení k internetu.');
    } finally {
      setIsRefreshingMyLocation(false);
    }
  };

  // Funkce pro aktualizaci GPS souřadnic pomocí geokódování
  const updateGpsCoordinates = async () => {
    const companiesWithoutGps = companies.filter(company => 
      (!company.lat || !company.lng) && 
      company.address
    );

    if (companiesWithoutGps.length === 0) {
      alert('Všechny firmy již mají GPS souřadnice nebo nemají adresu.');
      return;
    }

    if (!window.confirm(`Aktualizovat GPS souřadnice pro ${companiesWithoutGps.length} firem? Proces může chvíli trvat.`)) {
      return;
    }

    setIsUpdatingGps(true); // Začni loading
    setGpsUpdateProgress(0); // Reset pokroku
    let updated = 0;
    let failed = 0;

    for (let i = 0; i < companiesWithoutGps.length; i++) {
      const company = companiesWithoutGps[i];
      
      try {
        // Použij Nominatim API pro geokódování
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(company.address)}&countrycodes=cz&limit=1`
        );
        const data = await response.json();

        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);

          // Uložit souřadnice do databáze
          const updateResponse = await fetch(`${API_URL}/companies/${company._id}/coordinates`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ lat, lng })
          });

          if (updateResponse.ok) {
            updated++;
            console.log(`Aktualizovány GPS souřadnice pro ${company.name}: ${lat}, ${lng}`);
          } else {
            failed++;
            console.error(`Chyba při ukládání souřadnic pro ${company.name}`);
          }
        } else {
          failed++;
          console.log(`Nenalezeny souřadnice pro adresu: ${company.address}`);
        }

        // Aktualizuj pokrok
        const progress = Math.round(((i + 1) / companiesWithoutGps.length) * 100);
        setGpsUpdateProgress(progress);

        // Malé zpoždění mezi požadavky, aby se nepřetížil Nominatim
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        failed++;
        console.error(`Chyba při geokódování pro ${company.name}:`, error);
        
        // Aktualizuj pokrok i při chybě
        const progress = Math.round(((i + 1) / companiesWithoutGps.length) * 100);
        setGpsUpdateProgress(progress);
      }
    }

    setIsUpdatingGps(false); // Ukonči loading
    setGpsUpdateProgress(0); // Reset pokroku
    alert(`GPS souřadnice aktualizovány:\nÚspěšně: ${updated}\nNeúspěšně: ${failed}`);
    
    // Obnovit data firem
    if (onRefreshCompanies && updated > 0) {
      onRefreshCompanies();
    }
  };

  // Debugování - ukázat loading screen déle
  if (!currentPosition) {
    console.log('MeetingPlanner: Loading current position...');
    return (
      <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center shadow-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">Načítám schůzky...</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Získávám vaši polohu a připravuji mapu</p>
        </div>
      </div>
    );
  }

  console.log('MeetingPlanner: Rendering main component with position:', currentPosition);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${isDarkMode ? 'dark' : ''}`}>
      <div className="bg-white dark:bg-gray-800 w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-800 dark:hover:bg-blue-700 dark:text-white rounded-lg shadow-md transition font-semibold"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Zpět na seznam firem
            </button>
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white">🗺️ Schůzky</h2>
          </div>
          <div className="flex items-center gap-2">
            {showManualLocationInput ? (
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  type="text"
                  value={manualLocationAddress}
                  onChange={e => setManualLocationAddress(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') geocodeManualLocation(); if (e.key === 'Escape') setShowManualLocationInput(false); }}
                  placeholder="Zadejte adresu..."
                  className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={geocodeManualLocation}
                  disabled={isRefreshingMyLocation || !manualLocationAddress.trim()}
                  className="px-2 py-1.5 rounded text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  title="Najít adresu"
                >
                  {isRefreshingMyLocation ? (
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                  ) : '🔍'}
                </button>
                <button
                  onClick={() => setShowManualLocationInput(false)}
                  className="px-2 py-1.5 rounded text-sm text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500"
                  title="Zrušit"
                >✕</button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <button
                  onClick={refreshMyLocation}
                  disabled={isRefreshingMyLocation}
                  className={`px-3 py-2 rounded text-sm text-white transition-colors ${
                    isRefreshingMyLocation
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  title="Aktualizovat moji polohu přes GPS"
                >
                  {isRefreshingMyLocation ? (
                    <>
                      <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                      Načítám...
                    </>
                  ) : (
                    <>📡 Moje poloha</>
                  )}
                </button>
                <button
                  onClick={() => setShowManualLocationInput(true)}
                  disabled={isRefreshingMyLocation}
                  className="px-2 py-2 rounded text-sm text-white bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  title="Zadat adresu ručně"
                >✏️</button>
              </div>
            )}
            <button
              onClick={updateGpsCoordinates}
              disabled={isUpdatingGps}
              className={`px-3 py-2 rounded text-sm text-white transition-colors ${
                isUpdatingGps 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
              title={isUpdatingGps ? `Aktualizuji GPS souřadnice... ${gpsUpdateProgress}%` : "Aktualizovat chybějící GPS souřadnice pomocí geokódování"}
            >
              {isUpdatingGps ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Aktualizuji GPS... {gpsUpdateProgress}%
                </>
              ) : (
                <>📍 Aktualizovat GPS</>
              )}
            </button>
            {isRouteMode && (
              <>
                <button
                  onClick={saveCurrentRoute}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm"
                >
                  💾 Uložit trasu
                </button>
                <button
                  onClick={clearRouteMode}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded text-sm"
                >
                  🔄 Vyčistit trasu
                </button>
              </>
            )}
            
            {savedRoutes.length > 0 && (
              <button
                onClick={() => setShowLoadRouteModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm"
              >
                📂 Načíst trasu
              </button>
            )}
          </div>
        </div>

        {/* Progress bar pro GPS aktualizaci */}
        {isUpdatingGps && (
          <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Aktualizuji GPS souřadnice...
              </span>
              <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                {gpsUpdateProgress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${gpsUpdateProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Main content - responsivní layout */}
        <div className="flex-1 flex flex-col lg:flex-row min-h-0">
          {/* Mapa - na mobilu nahoře, na desktopu vlevo */}
          <div className="flex-1 relative min-h-0 h-1/2 lg:h-full">
            <MapContainer
              center={[currentPosition.lat, currentPosition.lng]}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url={isDarkMode 
                  ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                }
              />
              
              {/* Aktuální pozice */}
              <Marker 
                position={[currentPosition.lat, currentPosition.lng]}
                icon={currentLocationIcon}
              >
                <Popup>
                  <strong>Vaše aktuální pozice</strong><br />
                  📍 Lat: {currentPosition.lat.toFixed(4)}<br />
                  📍 Lng: {currentPosition.lng.toFixed(4)}
                </Popup>
              </Marker>

              {/* Všichni zákazníci */}
              {filteredCompanies.map(company => {
                if (!company.lat || !company.lng) return null;
                
                const isSelected = selectedCustomers.find(c => c._id === company._id);
                const isInRoute = routePoints.find(p => p.customerId === company._id);
                const isNearbyRoute = nearbyCustomers.find(c => c._id === company._id);
                const hasMeeting = plannedMeetings.find(m => m.companyId === company._id);
                
                let markerIcon;
                if (isInRoute) {
                  markerIcon = routeCustomerIcon;
                } else if (hasMeeting) {
                  markerIcon = meetingCustomerIcon; // Zelená pro zákazníky se schůzkou
                } else if (isNearbyRoute) {
                  markerIcon = nearbyRouteCustomerIcon; // Fialová pro zákazníky v okolí trasy
                } else if (isSelected) {
                  markerIcon = selectedCustomerIcon;
                } else {
                  markerIcon = customerIcon;
                }

                return (
                  <Marker
                    key={company._id}
                    position={[company.lat, company.lng]}
                    icon={markerIcon}
                  >
                    <Popup>
                      <div className="max-w-xs min-w-[280px]">
                        <h3 className="font-bold text-sm mb-1">{company.name}</h3>
                        <p className="text-xs text-gray-600 mb-1">{company.address}</p>
                        
                        {/* Status a IČO */}
                        <div className="grid grid-cols-2 gap-2 mb-1">
                          <p className="text-xs text-gray-500">
                            <strong>Stav:</strong> {company.status || 'Bez stavu'}
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>IČO:</strong> {company.ico}
                          </p>
                        </div>

                        {/* Kontakty s možností volání */}
                        {company.contacts && company.contacts.length > 0 && (
                          <div className="text-xs text-gray-500 mb-1 bg-gray-50 p-2 rounded">
                            <strong>Kontakty:</strong><br/>
                            {company.contacts.map((contact, index) => (
                              <div key={index} className="mb-0.5 flex items-center justify-between">
                                <div>
                                  📞 <a 
                                    href={`tel:${contact.phone}`} 
                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                    title="Kliknutím zavoláte"
                                  >
                                    {contact.phone}
                                  </a> ({contact.name || 'Kontakt'})<br/>
                                  {contact.email && (
                                    <span>📧 <a 
                                      href={`mailto:${contact.email}`} 
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      {contact.email}
                                    </a></span>
                                  )}
                                </div>
                                <button
                                  onClick={() => window.open(`tel:${contact.phone}`)}
                                  className="bg-green-500 hover:bg-green-600 text-white px-1 py-0.5 rounded text-xs ml-1"
                                  title="Zavolat"
                                >
                                  📞
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Poslední aktivita */}
                        {company.lastActivity && (
                          <div className="text-xs text-gray-500 mb-1 bg-blue-50 p-2 rounded">
                            <strong>Poslední aktivita:</strong><br/>
                            📅 {new Date(company.lastActivity.date).toLocaleDateString('cs-CZ')}<br/>
                            📝 {company.lastActivity.type || 'Aktivita'}: {company.lastActivity.note || 'Bez poznámky'}
                          </div>
                        )}

                        <div className="mt-2 flex gap-1">
                          <button
                            onClick={() => planRoute(company)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs flex-1"
                          >
                            + Trasa
                          </button>
                          <button
                            onClick={() => openScheduleModal(company)}
                            className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs flex-1"
                          >
                            🤝 Schůzka
                          </button>
                          <button
                            onClick={() => window.open(`#/companies/${company._id}`, '_blank')}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs flex-1"
                          >
                            📋 Detail
                          </button>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* Routing */}
              {routePoints.length >= 2 && (
                <RoutingMachine 
                  routePoints={routePoints} 
                  onRouteFound={handleRouteFound}
                />
              )}
              
              {/* Automatické centrování mapy na načtenou trasu */}
              <FitBounds 
                routePoints={routePoints} 
                shouldFitBounds={shouldFitBounds}
                onFitComplete={() => setShouldFitBounds(false)}
              />
            </MapContainer>

            {/* Informace o trase */}
            {routeInfo && (
              <div className="absolute top-4 left-4 bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg max-w-64">
                <h3 className="font-bold text-gray-800 dark:text-white mb-2">📊 Informace o trase</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  📏 Vzdálenost: {(routeInfo.distance / 1000).toFixed(1)} km<br/>
                  ⏱️ Čas: {Math.round(routeInfo.time / 60)} min<br/>
                  🎯 Zastávky: {routePoints.length - 1}
                </p>
              </div>
            )}
          </div>

          {/* Pravý panel - na mobilu dole, na desktopu vpravo */}
          <div className="lg:w-[900px] h-1/2 lg:h-full border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-600 flex flex-shrink-0 max-h-full">
            
            {/* Levý sloupec - Vybraní zákazníci a v okolí trasy */}
            <div className="w-1/2 lg:w-[450px] flex flex-col max-h-full border-r border-gray-200 dark:border-gray-600">
              {/* Header levého sloupce */}
              <div className="p-2 lg:p-4 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
                <h3 className="text-sm lg:text-lg font-semibold text-gray-800 dark:text-white mb-1 lg:mb-2">
                  📍 Vybraní a v okolí trasy
                </h3>
                
                {/* Nastavení perimetru pro hledání zákazníků v okolí trasy */}
                {isRouteMode && (
                  <div className="mb-2 lg:mb-3">
                    <label className="text-xs lg:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                      🎯 Perimetr hledání (km):
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      value={filterRadius}
                      onChange={(e) => setFilterRadius(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 mb-1"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>1 km</span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">{filterRadius} km</span>
                      <span>50 km</span>
                    </div>
                  </div>
                )}
                
                {/* Statistiky */}
                {isRouteMode && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 lg:gap-2 text-xs lg:text-sm">
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-1 lg:p-2 rounded">
                      <div className="text-blue-600 dark:text-blue-400 font-semibold">
                        {routePoints.length}
                      </div>
                      <div className="text-gray-600 dark:text-gray-300 text-xs">
                        Body trasy
                      </div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/30 p-1 lg:p-2 rounded">
                      <div className="text-green-600 dark:text-green-400 font-semibold">
                        {Math.round(routeInfo?.distance / 1000 || 0)} km
                      </div>
                      <div className="text-gray-600 dark:text-gray-300 text-xs">
                        Vzdálenost
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Obsah levého sloupce */}
              <div className="flex-1 overflow-y-auto p-2 lg:p-4">
                {/* Vybraní zákazníci v trase */}
                {isRouteMode && routePoints.length > 0 && (
                  <div className="mb-3 lg:mb-6">
                    <h4 className="text-sm lg:text-md font-semibold text-gray-700 dark:text-gray-300 mb-2 lg:mb-3">
                      🛣️ Trasa ({routePoints.length})
                    </h4>
                    <div className="space-y-1 lg:space-y-2">
                      {routePoints.map((point, index) => (
                        <div key={`route-${index}`} className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-2 lg:p-3">
                          <div className="flex items-start justify-between mb-1 lg:mb-2">
                            <div className="flex items-start gap-1 lg:gap-2 flex-1 min-w-0">
                              <span className="bg-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center flex-shrink-0 mt-0.5 lg:mt-1">
                                {index + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-800 dark:text-white text-xs lg:text-sm truncate">
                                  {point.company.name}
                                </div>
                                <div className="text-gray-600 dark:text-gray-300 text-xs truncate hidden lg:block">
                                  {point.company.address}
                                </div>
                                {point.company.ico && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden lg:block">
                                    IČO: {point.company.ico}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-0.5 lg:gap-1 flex-shrink-0">
                              {/* Ovládání pořadí - úplně vlevo, pouze pro zákazníky, ne pro výchozí pozici */}
                              {point.type === 'customer' && (
                                <div className="flex flex-col gap-0.5 mr-1 lg:mr-2">
                                  <button
                                    onClick={() => moveRoutePointUp(index)}
                                    disabled={index <= 1}
                                    className={`text-xs px-1 lg:px-1.5 py-0.5 rounded ${
                                      index <= 1 
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                                    }`}
                                    title="Posunout nahoru"
                                  >
                                    ↑
                                  </button>
                                  <button
                                    onClick={() => moveRoutePointDown(index)}
                                    disabled={index >= routePoints.length - 1}
                                    className={`text-xs px-1 lg:px-1.5 py-0.5 rounded ${
                                      index >= routePoints.length - 1 
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                                    }`}
                                    title="Posunout dolů"
                                  >
                                    ↓
                                  </button>
                                </div>
                              )}
                              <button
                                onClick={() => openScheduleModal(point.company, index + 1)}
                                className="bg-green-600 hover:bg-green-700 text-white px-1 lg:px-2 py-1 rounded text-xs"
                                title="Naplánovat schůzku"
                              >
                                📅
                              </button>
                              <button
                                onClick={() => window.open(`#/companies/${point.company._id}`, '_blank')}
                                className="bg-gray-600 hover:bg-gray-700 text-white px-1 lg:px-2 py-1 rounded text-xs hidden lg:inline-block"
                                title="Detail společnosti"
                              >
                                📋
                              </button>
                              {point.type === 'customer' && (
                                <button
                                  onClick={() => removeFromRoute(index)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-1 lg:px-2 py-1 rounded text-xs"
                                  title="Odstranit z trasy"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Detaily pouze pro skutečné zákazníky, ne pro výchozí pozici */}
                          {point.type === 'customer' && (
                            <>
                              {point.company.contacts && point.company.contacts.length > 0 && (
                                <div className="mb-2 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Kontakty:</div>
                                  {point.company.contacts.map((contact, contactIndex) => (
                                    <div key={contactIndex} className="flex items-center justify-between mb-1">
                                      <div className="flex-1">
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                          📞 <a 
                                            href={`tel:${contact.phone}`} 
                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                          >
                                            {contact.phone}
                                          </a> ({contact.name || 'Kontakt'})
                                          {contact.email && (
                                            <span className="block">📧 <a 
                                              href={`mailto:${contact.email}`} 
                                              className="text-blue-600 hover:text-blue-800"
                                            >
                                              {contact.email}
                                            </a></span>
                                          )}
                                        </p>
                                      </div>
                                      <button
                                        onClick={() => window.open(`tel:${contact.phone}`)}
                                        className="bg-green-500 hover:bg-green-600 text-white px-1 py-0.5 rounded text-xs ml-1"
                                        title="Zavolat"
                                      >
                                        📞
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {point.company.status && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                  {getStatusEmoji(point.company.status)} {point.company.status}
                                </p>
                              )}
                              {point.company.nextActionDate && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                  📅 Další akce: {new Date(point.company.nextActionDate).toLocaleDateString('cs-CZ')}
                                </p>
                              )}
                              <div className="border-t border-blue-200 dark:border-blue-700 pt-2 mt-2">
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  <strong>Poslední aktivita:</strong><br/>
                                  {getLastActivity(point.company)}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Zákazníci v okolí trasy */}
                {isRouteMode && nearbyCustomers.length > 0 && (
                  <div className="mb-3 lg:mb-6">
                    <h4 className="text-sm lg:text-md font-semibold text-gray-700 dark:text-gray-300 mb-2 lg:mb-3">
                      🟣 V okolí ({nearbyCustomers.length})
                    </h4>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 lg:mb-3 hidden lg:block">
                      Perimetr: {filterRadius} km od trasy
                    </div>
                    <div className="space-y-1 lg:space-y-2">
                      {nearbyCustomers.map(company => (
                        <div key={`nearby-${company._id}`} className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg p-2 lg:p-3">
                          <div className="flex items-start justify-between mb-1 lg:mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-800 dark:text-white text-xs lg:text-sm truncate">
                                {company.name}
                              </div>
                              <div className="text-gray-600 dark:text-gray-300 text-xs truncate hidden lg:block">
                                {company.address}
                              </div>
                              {company.ico && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden lg:block">
                                  IČO: {company.ico}
                                </div>
                              )}
                              <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                📏 {company.distanceFromRoute.toFixed(1)} km
                              </div>
                            </div>
                          </div>
                          
                          {/* Kontakty - jednotný formát s klikatelnými odkazy */}
                          {company.contacts && company.contacts.length > 0 && (
                            <div className="mb-2 bg-purple-100 dark:bg-purple-800/50 p-2 rounded hidden lg:block">
                              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Kontakty:</div>
                              {company.contacts.map((contact, contactIndex) => (
                                <div key={contactIndex} className="flex items-center justify-between mb-1">
                                  <div className="flex-1">
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      📞 <a 
                                        href={`tel:${contact.phone}`} 
                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                      >
                                        {contact.phone}
                                      </a> ({contact.name || 'Kontakt'})
                                      {contact.email && (
                                        <span className="block">📧 <a 
                                          href={`mailto:${contact.email}`} 
                                          className="text-blue-600 hover:text-blue-800"
                                        >
                                          {contact.email}
                                        </a></span>
                                      )}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => window.open(`tel:${contact.phone}`)}
                                    className="bg-green-500 hover:bg-green-600 text-white px-1 py-0.5 rounded text-xs ml-1"
                                    title="Zavolat"
                                  >
                                    �
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Stav - jednotné umístění pod kontakty */}
                          {company.status && (
                            <div className="text-xs text-gray-600 dark:text-gray-300 mb-1 lg:mb-2 hidden lg:block">
                              {getStatusEmoji(company.status)} {company.status}
                            </div>
                          )}
                          
                          {/* Další akce */}
                          {company.nextActionDate && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 lg:mb-2 hidden lg:block">
                              📅 Další akce: {new Date(company.nextActionDate).toLocaleDateString('cs-CZ')}
                            </p>
                          )}
                          
                          {/* Poslední aktivita */}
                          <div className="border-t border-purple-200 dark:border-purple-700 pt-2 mt-2 mb-2 hidden lg:block">
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              <strong>Poslední aktivita:</strong><br/>
                              {getLastActivity(company)}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-1 lg:gap-2">
                            <button
                              onClick={() => addToRoute(company)}
                              className="bg-purple-600 hover:bg-purple-700 text-white px-1 lg:px-2 py-1 rounded text-xs flex-1"
                              title="Přidat do trasy"
                            >
                              <span className="lg:hidden">+</span>
                              <span className="hidden lg:inline">+ Trasa</span>
                            </button>
                            <button
                              onClick={() => openScheduleModal(company)}
                              className="bg-green-600 hover:bg-green-700 text-white px-1 lg:px-2 py-1 rounded text-xs flex-1"
                              title="Naplánovat schůzku"
                            >
                              <span className="lg:hidden">🤝</span>
                              <span className="hidden lg:inline">🤝 Schůzka</span>
                            </button>
                            <button
                              onClick={() => window.open(`#/companies/${company._id}`, '_blank')}
                              className="bg-gray-600 hover:bg-gray-700 text-white px-1 lg:px-2 py-1 rounded text-xs flex-1 hidden lg:block"
                            >
                              📋 Detail
                            </button>
                          </div>
                          
                          {/* Poslední aktivita */}
                          <div className="border-t border-purple-200 dark:border-purple-700 pt-2 mt-2 mb-2 hidden lg:block">
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              <strong>Poslední aktivita:</strong><br/>
                              {getLastActivity(company)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prázdný stav */}
                {!isRouteMode && (
                  <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                    <div className="text-4xl mb-2">🗺️</div>
                    <p className="text-sm">Klikněte na "Trasa" pro zobrazení vybraných zákazníků</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pravý sloupec - Všichni zákazníci */}
            <div className="w-1/2 lg:w-[450px] flex flex-col max-h-full">
              {/* Header pravého sloupce */}
              <div className="p-2 lg:p-4 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
                <h3 className="text-sm lg:text-lg font-semibold text-gray-800 dark:text-white mb-1 lg:mb-2">
                  👥 Všichni zákazníci
                </h3>

                {/* Status Filter Dropdown */}
                {statusFilter && uniqueStatuses && (
                  <div className="relative mb-2 lg:mb-3 status-dropdown-container">
                    <button
                      className={`w-full px-2 lg:px-3 py-1 lg:py-2 text-xs lg:text-sm rounded-lg font-semibold transition ${
                        statusFilter.length > 0
                          ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                          : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200'
                      }`}
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    >
                      📋 Filtr stavů {statusFilter.length > 0 && `(${statusFilter.length})`}
                    </button>
                    
                    {showStatusDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50">
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs lg:text-sm font-medium text-gray-700 dark:text-gray-300">Filtr podle stavů:</span>
                            {statusFilter.length > 0 && (
                              <button
                                onClick={() => {
                                  // Vynulujeme všechny statusy
                                  statusFilter.forEach(status => toggleStatusFilter(status));
                                }}
                                className="text-xs text-red-600 hover:text-red-800 dark:text-red-400"
                              >
                                Vymazat ({statusFilter.length})
                              </button>
                            )}
                          </div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {uniqueStatuses.map(status => (
                              <label key={status} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                                <input
                                  type="checkbox"
                                  checked={statusFilter.includes(status)}
                                  onChange={() => toggleStatusFilter(status)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs lg:text-sm text-gray-900 dark:text-white">
                                  {getStatusEmoji && getStatusEmoji(status)} {status}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <input
                  type="text"
                  placeholder="Hledat zákazníka..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-2 lg:px-3 py-1 lg:py-2 text-xs lg:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2 lg:mb-3"
                />

                {/* Statistiky */}
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Všichni: {filteredCompanies.length} | 
                  📍 S GPS: {filteredCompanies.filter(c => c.lat && c.lng).length} | 
                  ❌ Bez GPS: {filteredCompanies.filter(c => !c.lat || !c.lng).length} | 
                  🟣 V okolí: {nearbyCustomers.length}
                </div>
              </div>

              {/* Seznam všech zákazníků */}
              <div className="flex-1 overflow-y-auto p-2 lg:p-4">
                <div className="space-y-1 lg:space-y-2">
                  {filteredCompanies.map(company => (
                    <div key={company._id} className="bg-white dark:bg-gray-700 p-2 lg:p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-1 lg:mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-800 dark:text-white text-xs lg:text-sm truncate">{company.name}</h4>
                          <p className="text-xs text-gray-600 dark:text-gray-300 hidden lg:block">{company.address}</p>
                          {company.ico && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 hidden lg:block">IČO: {company.ico}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          {company.lat && company.lng ? (
                            <span className="text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-1 lg:px-2 py-1 rounded">
                              📍
                            </span>
                          ) : (
                            <span className="text-xs bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-1 lg:px-2 py-1 rounded" title="Chybí GPS souřadnice - firma se nezobrazí na mapě">
                              ❌ GPS
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Kontakty - jednotný formát s klikatelnými odkazy */}
                      {company.contacts && company.contacts.length > 0 && (
                        <div className="mb-2 bg-gray-50 dark:bg-gray-800 p-2 rounded hidden lg:block">
                          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Kontakty:</div>
                          {company.contacts.map((contact, contactIndex) => (
                            <div key={contactIndex} className="flex items-center justify-between mb-1">
                              <div className="flex-1">
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  📞 <a 
                                    href={`tel:${contact.phone}`} 
                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                  >
                                    {contact.phone}
                                  </a> ({contact.name || 'Kontakt'})
                                  {contact.email && (
                                    <span className="block">📧 <a 
                                      href={`mailto:${contact.email}`} 
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      {contact.email}
                                    </a></span>
                                  )}
                                </p>
                              </div>
                              <button
                                onClick={() => window.open(`tel:${contact.phone}`)}
                                className="bg-green-500 hover:bg-green-600 text-white px-1 py-0.5 rounded text-xs ml-1"
                                title="Zavolat"
                              >
                                📞
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Stav - jednotné umístění pod kontakty */}
                      {company.status && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 lg:mb-2">
                          {getStatusEmoji(company.status)} <span className="hidden lg:inline">{company.status}</span>
                        </div>
                      )}
                      
                      {/* Další akce */}
                      {company.nextActionDate && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 lg:mb-2 hidden lg:block">
                          📅 Další akce: {new Date(company.nextActionDate).toLocaleDateString('cs-CZ')}
                        </p>
                      )}
                      
                      {/* Poslední aktivita */}
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-1 lg:pt-2 mb-1 lg:mb-2 hidden lg:block">
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          <strong>Poslední aktivita:</strong><br/>
                          {getLastActivity(company)}
                        </p>
                      </div>
                      
                      <div className="flex gap-1">
                        {/* Tlačítko + Trasa - umožnit i vytvoření první trasy */}
                        {company.lat && company.lng ? (
                          !routePoints.find(p => p.customerId === company._id) && (
                            <button
                              onClick={() => addToRoute(company)}
                              className="bg-purple-600 hover:bg-purple-700 text-white px-1 lg:px-2 py-1 rounded text-xs flex-1"
                              title="Přidat do trasy"
                            >
                              <span className="lg:hidden">+</span>
                              <span className="hidden lg:inline">+ Trasa</span>
                            </button>
                          )
                        ) : (
                          <button
                            onClick={() => alert('Nelze přidat do trasy - chybí GPS souřadnice pro tuto firmu')}
                            className="bg-gray-400 cursor-not-allowed text-white px-1 lg:px-2 py-1 rounded text-xs flex-1"
                            title="Chybí GPS souřadnice"
                            disabled
                          >
                            <span className="lg:hidden">❌</span>
                            <span className="hidden lg:inline">❌ GPS</span>
                          </button>
                        )}
                        <button
                          onClick={() => openScheduleModal(company)}
                          className="bg-green-600 hover:bg-green-700 text-white px-1 lg:px-2 py-1 rounded text-xs flex-1"
                          title="Naplánovat schůzku"
                        >
                          <span className="lg:hidden">🤝</span>
                          <span className="hidden lg:inline">🤝 Schůzka</span>
                        </button>
                        <button
                          onClick={() => window.open(`#/companies/${company._id}`, '_blank')}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-1 lg:px-2 py-1 rounded text-xs flex-1"
                          title="Detail společnosti"
                        >
                          <span className="lg:hidden">📋</span>
                          <span className="hidden lg:inline">📋 Detail</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal pro uložení trasy */}
        {showSaveRouteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130]">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                💾 Uložit trasu
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Název trasy:
                </label>
                <input
                  type="text"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  placeholder="Zadejte název trasy..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleSaveRoute}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex-1"
                >
                  💾 Uložit
                </button>
                <button
                  onClick={() => {
                    setShowSaveRouteModal(false);
                    setRouteName('');
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded flex-1"
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal pro načtení trasy */}
        {showLoadRouteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130]">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                📂 Načíst uloženou trasu
              </h3>
              
              {savedRoutes.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  Nemáte žádné uložené trasy
                </p>
              ) : (
                <div className="space-y-3 mb-4">
                  {savedRoutes.map(route => (
                    <div key={route.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800 dark:text-white">
                            {route.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            Vytvořeno: {new Date(route.createdAt).toLocaleDateString('cs-CZ', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Bodů trasy: {route.routePoints?.length || 0} | 
                            Vzdálenost: {route.routeInfo ? Math.round(route.routeInfo.distance / 1000) : '?'} km
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => loadRoute(route)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                          >
                            📂 Načíst
                          </button>
                          <button
                            onClick={() => deleteRoute(route.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                          >
                            🗑️ Smazat
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowLoadRouteModal(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                >
                  Zavřít
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Modal */}
        <ScheduleModal
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedCustomerForSchedule(null);
            setRouteOrderForSchedule(0);
          }}
          onSave={savePlannedMeeting}
          customer={selectedCustomerForSchedule}
          routeOrder={routeOrderForSchedule}
        />
      </div>
    </div>
  );
};

export default MeetingPlanner;
